import Link from "next/link";
import { getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";
import Image from "next/image";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function Accessibility({ metadata }) {
  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-12 lg:gap-spacing-14 text-sm lg:text-base">
        <Breadcrumb />
        <p className="p-spacing-7 lg:p-spacing-9 bg-onmoPurple-50 text-onmoText rounded-xl text-center 4xl:text-xl">
          At Onmo, we aim to ensure that all our customers have equal access to
          the information and features of our website. With this in mind, our
          website has been built to comply with Web Content Accessibility
          Guidelines (WCAG) 2.2 (w3.org).
        </p>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/book.svg"
            alt="book-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Register your Accessibility and Additional Support needs
            </h3>
            <p>
              At Onmo we understand that some of our customers may need extra
              due care and support due to changes in health and life events. We
              therefore welcome you to contact us and advise us of any
              accessibility or additional support needs you may have.
            </p>
            <p className="mt-6">
              With your consent, we can then make a note of these requirements
              on your account to enable us to support you whilst being an Onmo
              customer and avoid you having to repeat these needs to us each
              time you contact us. We will only ever use this information to
              ensure we provide you with the best possible support. If your
              circumstances change, and you no longer need additional support,
              please{" "}
              <Link
                className="text-onmoPrimary underline"
                target="_blank"
                href="/contact-us"
              >
                contact us
              </Link>{" "}
              so we can update your records. Please access our Privacy Policy
              for more information.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/headphone.svg"
            alt="headphone-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium  mb-2">
              Hearing and Speech Impairment
            </h3>
            <p>
              If you have a hearing or speech impairment and calling us is not
              an option for you, you can contact by:
            </p>
            <div className="mt-6">
              <ul className="list-disc pl-6">
                <li>Sending an email to help@onmo.app</li>
                <li>Sending message via the Onmo App</li>
                <li>Using live chat via our the Onmo website</li>
              </ul>
            </div>
            <p className="mt-6">
              Alternatively, you can contact us by using{" "}
              <Link
                className="text-onmoPrimary underline"
                href="https://www.relayuk.bt.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {" "}
                Relay UK.
              </Link>
            </p>
          </div>
        </div>

        <div className="flex gap-3 items-start flex-col md:flex-row lg:flex-row 4xl:text-xl">
          <Image
            src="/images/icons/show-password.svg"
            alt="eye-icon"
            className="m-1 ml-0 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl font-medium mb-2">
              Visual Impairment
            </h3>
            <p>
              If you have a visual impairment, we can offer the following
              support:
            </p>
            <p className="mt-6">
              Large Print documents: You can request for any formal
              communications we send customers via post to be sent to you in
              large print.
            </p>
            <p className="mt-6">
              If you require any of our legal documents to be sent to you in
              large print, please{" "}
              <Link
                href="/contact-us"
                passHref
                className="text-onmoPrimary underline"
              >
                {" "}
                Contact Us
              </Link>
            </p>
          </div>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Accessibility`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
